import React, { useMemo, useCallback, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import * as yup from "yup";
import { useTranslation } from "react-i18next";

import clsx from "clsx";

import map from "lodash/map";
import get from "lodash/get";
import AsyncSelect from "react-select/async";

import { useRouteMatch, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";

import {
  UploadInput,
  UploadPreview,
  useFiles,
} from "../../../../components/FileUpload";
import Input from "../../../../components/Input";
import RoleBlock from "../../../../components/RoleBlock";

import {
  QUERY_NAMEBOARD_ITEM,
  QUERY_NAMEBOARD_ITEMS,
  QUERY_ENTITIES_PARENTS,
} from "../../../../config/graphql/query";

import {
  CREATE_NAMEBOARD_ITEM,
  UPDATE_NAMEBOARD_ITEM,
  DELETE_NAMEBOARD_ITEM,
} from "../../../../config/graphql/mutation";

import { EntityType } from "../../../../config/const/entity";

const ItemRoute = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const { t } = useTranslation(["screens", "common"]);

  const {
    params: { screenId: nameboard, id },
  }: { params: { screenId: string; id?: string } } = useRouteMatch();

  const { replace } = useHistory();
  const graphql = useApolloClient();

  const [onSave, { loading: saving }] = useMutation(
    id ? UPDATE_NAMEBOARD_ITEM : CREATE_NAMEBOARD_ITEM,
    {
      refetchQueries: [
        {
          query: QUERY_NAMEBOARD_ITEMS,
          variables: {
            filter: {
              nameboard,
            },
          },
        },
      ],
    },
  );

  const [files, { onUpload: onUploadPathImage }] = useFiles("plan");

  const schema = useMemo(
    () =>
      yup.object().shape({
        entity: yup.object().shape({
          id: yup.string().required(t("screens:screen.items.item.yup.entity")),
        }),
      }),
    [t],
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const { data: nameboardItemData, loading } = useQuery(QUERY_NAMEBOARD_ITEM, {
    skip: !id,
    variables: { id },
  });

  useEffect(() => {
    methods.reset(nameboardItemData?.nameboardItem ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameboardItemData]);

  const [onDeleteItem] = useMutation(DELETE_NAMEBOARD_ITEM, {
    refetchQueries: [
      {
        query: QUERY_NAMEBOARD_ITEMS,
        variables: {
          filter: {
            nameboard,
          },
        },
      },
    ],
  });

  const onRemoveItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDeleteItem({ variables: { id } })
        .then(() => {
          toast.success<string>(t("screens:screen.items.item.toast.deleted"));
          replace(`/nameboards/${nameboard}/item`);
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [onDeleteItem, replace, nameboard, id, t],
  );

  const onBeforeRemoveItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      setShow(true);
    },
    [setShow],
  );

  const onSubmit = useCallback(
    async ({ entity, sort, ...rest }: any) => {
      let input = {
        ...rest,
        sort: sort ? parseInt(sort, 10) : undefined,
        nameboard,
        entity: entity.id,
        ...(!!id && { id }),
      };

      if (Array.isArray(files) && files.length > 0) {
        const uploadedFiles = await onUploadPathImage();
        if (uploadedFiles && uploadedFiles[0]) {
          input = { ...input, plan: uploadedFiles[0].file.id };
        }
      }

      if (id) {
        return onSave({ variables: { input } })
          .then(({ data }) => {
            replace(
              `/nameboards/${nameboard}/item/${data?.addNameboardItem?.id}`,
            );
            toast.success<string>(t("screens:screen.items.item.toast.updated"));
          })
          .catch((error) => {
            toast.error<string>(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message,
            );
          });
      }

      return onSave({
        variables: {
          input,
        },
      })
        .then(({ data }) => {
          replace(
            `/nameboards/${nameboard}/item/${data?.addNameboardItem?.id}`,
          );

          if (data?.addNameboardItem?.id) {
            toast.success<string>(t("screens:screen.items.item.toast.created"));
            return;
          }

          toast.success<string>(t("screens:screen.items.item.toast.updated"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [id, files, nameboard, onUploadPathImage, replace, onSave, t],
  );

  const getOptionLabel = useCallback(
    ({ title, properties, parents }: TEntity) => {
      const propertiesString =
        map(properties, "title").filter(Boolean).join(", ") || "N/A";

      const entityTitle = [...map(parents, "title").reverse(), title]
        .filter(Boolean)
        .join(" - ");

      return [propertiesString, `(${entityTitle})`].filter(Boolean).join(" - ");
    },
    [],
  );

  const onLoadOptions = useCallback(
    async (value: string) => {
      try {
        const {
          data: { nameboardItems },
        } = await graphql.query({
          query: QUERY_NAMEBOARD_ITEMS,
          variables: { filter: { nameboard } },
          fetchPolicy: "network-only",
        });

        const {
          data: { entities },
        } = await graphql.query({
          query: QUERY_ENTITIES_PARENTS,
          variables: {
            filter: {
              propertyTitle: value,
              type: EntityType.Space,
            },
          },
          fetchPolicy: "network-only",
        });

        return (
          entities.filter(
            (entity: any) =>
              !nameboardItems.some((item: any) => item.entity.id === entity.id),
          ) ?? []
        );
      } catch (error) {
        return [];
      }
    },
    [graphql, nameboard],
  );

  return (
    <>
      <FormProvider {...methods}>
        <div className="tab-pane active" role="tabpanel">
          <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="col-lg-4">
              <div className="form-group">
                <label htmlFor="entity">
                  {t("screens:screen.items.item.form.entity")}
                </label>
                <Controller
                  name="entity"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <AsyncSelect
                        defaultOptions
                        className={clsx({
                          "is-invalid": !!error,
                        })}
                        // closeMenuOnSelect={false}
                        getOptionLabel={getOptionLabel}
                        getOptionValue={({ id }) => id}
                        loadOptions={onLoadOptions}
                        {...field}
                      />
                      {!!error && (
                        <div className="invalid-feedback">{error.message}</div>
                      )}
                    </>
                  )}
                />
                {!!get(methods, "errors.entity.id") && (
                  <div className="invalid-feedback">
                    {(get(methods, "errors.entity.id") as any)?.message}
                  </div>
                )}
                <small id="emailHelp" className="form-text text-muted">
                  {t("screens:screen.items.item.form.emailHelp")}
                </small>
              </div>
              <RoleBlock roles={["ADMIN"]}>
                <div className="form-group">
                  <label htmlFor="sort">
                    {t("screens:screen.items.item.form.sort")}
                  </label>
                  <Input name="sort" className="form-control" />
                </div>
              </RoleBlock>
              <div className="form-group">
                <label htmlFor="plan">
                  {t("screens:screen.items.item.form.plan")}
                </label>
                <UploadInput name="plan" />
                <UploadPreview
                  name="plan"
                  placeholder={
                    nameboardItemData?.nameboardItem?.plan?.absolutePath
                  }
                />
              </div>
              {/* <div className="form-group">
              <label htmlFor="type">Direction</label>
              <select
                {...methods.register("direction")}
                name="direction"
                className="custom-select"
              >
                {Directions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="distance">Distance</label>
              <Input name="distance" className="form-control" />
            </div> */}
            </div>
            <div className="col-12">
              <input
                type="submit"
                className="btn btn-primary"
                disabled={saving || loading}
              />
              {id && (
                <button
                  onClick={onBeforeRemoveItem}
                  className="btn btn-danger ml-3"
                >
                  {t("common:delete")}
                </button>
              )}
            </div>
          </form>
        </div>
      </FormProvider>
      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("screens:screen.items.item.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("screens:screen.items.item.modal.body")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemoveItem}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default ItemRoute;
